import { Auth, Hub } from 'aws-amplify';
import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AppBannersProvider } from './components/alliance/app-banners';
import { AllianceErrorBoundary } from './components/alliance/error-boundary';
import { CrashScreen } from './components/alliance/system-screens';
import { DevCookieBtn } from './components/dev-cookie-btn';
import { AppProvider } from './contexts/app-context';
import { AuthContext } from './contexts/auth-context';
import { Root } from './routes/root';
import { captureError } from './utils/capture-error';

export function App() {
  // Context
  const { dispatch } = React.useContext(AuthContext);

  React.useEffect(() => {
    // Amplify has a local eventing system called Hub
    // Amplify’s Auth category publishes in the auth channel
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          dispatch({ type: 'USER_SET', payload: data });
          break;
        case 'tokenRefresh_failure':
          dispatch({ type: 'TOKEN_ERROR' });
          break;
        case 'signOut':
          dispatch({ type: 'USER_CLEAR' });
          break;
      }
    });

    const checkForUser = async () => {
      try {
        dispatch({ type: 'SET_CHECKING', payload: true });

        // Used to check if a user is logged in when the page is loaded
        const user = await Auth.currentAuthenticatedUser();
        dispatch({ type: 'USER_SET', payload: user });

        // Provides decoded JWT Tokens
        const userSession = await Auth.currentSession();
        dispatch({ type: 'SESSION_SET', payload: userSession });
      } catch (error) {
        // Will throw an error if there is no user logged in
        dispatch({ type: 'USER_CLEAR' });
      } finally {
        dispatch({ type: 'SET_CHECKING', payload: false });
      }
    };

    checkForUser();
  }, [dispatch]);

  return (
    // The last line of defence: a crash outside any route boundary (the shell
    // itself) shows the full-page crash screen instead of a blank page
    <AllianceErrorBoundary
      onError={(error, errorInfo) =>
        captureError({ data: { error, errorInfo } })
      }
      fallback={({ reset }) => (
        <CrashScreen
          layout="page"
          onRetry={reset}
          onHome={() => window.location.assign('/home')}
        />
      )}
    >
      <AppProvider>
        <DevCookieBtn />

        <AppBannersProvider>
          <Router>
            <Root />
          </Router>
        </AppBannersProvider>
      </AppProvider>
    </AllianceErrorBoundary>
  );
}
